export type ConversionLineaState = {
  /** Tope del renglón (Cantidad + Cantidad B). */
  total: number;
  /** Remanente en el tenant origen. */
  cantidad: number;
  /** Porción a enviar al otro tenant. */
  cantidadB: number;
  /** Porcentaje de Cantidad B sobre el total (entero hacia abajo). */
  porcentaje: number;
};

const floorNonNeg = (n: number): number => {
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.floor(n);
};

const clamp = (n: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, n));

export function porcentajeDesdeCantidades(cantidadB: number, total: number): number {
  const t = floorNonNeg(total);
  if (t <= 0) return 0;
  return floorNonNeg((floorNonNeg(cantidadB) / t) * 100);
}

export function initConversionLinea(
  cantidad: number | null | undefined,
  cantidadB?: number | null,
  porcentaje?: number | null,
): ConversionLineaState {
  const b = floorNonNeg(Number(cantidadB ?? 0));
  const cant = floorNonNeg(Number(cantidad ?? 0));
  const total = cant + b;
  const pctFromArgs = porcentaje != null && Number(porcentaje) > 0
    ? floorNonNeg(Number(porcentaje))
    : null;
  return {
    total,
    cantidad: cant,
    cantidadB: b,
    porcentaje: pctFromArgs != null ? clamp(pctFromArgs, 0, 100) : porcentajeDesdeCantidades(b, total),
  };
}

/** Cambia Cantidad B (0…total); recalcula remanente y %. */
export function aplicarCantidadB(
  state: ConversionLineaState,
  cantidadBNueva: number,
): ConversionLineaState {
  const total = floorNonNeg(state.total);
  const b = clamp(floorNonNeg(cantidadBNueva), 0, total);
  return {
    total,
    cantidad: total - b,
    cantidadB: b,
    porcentaje: porcentajeDesdeCantidades(b, total),
  };
}

/** Cambia el remanente (0…total); recalcula B y %. */
export function aplicarCantidad(
  state: ConversionLineaState,
  cantidadNueva: number,
): ConversionLineaState {
  const total = floorNonNeg(state.total);
  const cant = clamp(floorNonNeg(cantidadNueva), 0, total);
  const b = total - cant;
  return {
    total,
    cantidad: cant,
    cantidadB: b,
    porcentaje: porcentajeDesdeCantidades(b, total),
  };
}

/** Cambia el % (0…100); recalcula B y remanente. */
export function aplicarPorcentaje(
  state: ConversionLineaState,
  porcentajeNuevo: number,
): ConversionLineaState {
  const total = floorNonNeg(state.total);
  const pct = clamp(floorNonNeg(porcentajeNuevo), 0, 100);
  const b = clamp(floorNonNeg((total * pct) / 100), 0, total);
  return {
    total,
    cantidad: total - b,
    cantidadB: b,
    porcentaje: pct,
  };
}

/**
 * Arma el estado de una línea a partir de lo persistido.
 * Si hay %, recalcula Cantidad/Cant.B con floor sobre el total (cant+B),
 * para no heredar redondeos “hacia arriba” del backend.
 */
export function repartirLineaConversion(
  cantidad: number | null | undefined,
  cantidadB?: number | null,
  porcentaje?: number | null,
): ConversionLineaState {
  const base = initConversionLinea(cantidad, cantidadB, null);
  const pct = porcentaje != null && Number(porcentaje) > 0
    ? floorNonNeg(Number(porcentaje))
    : 0;
  if (pct > 0) return aplicarPorcentaje(base, pct);
  return base;
}

/** % del cliente si es mayor a 0; si no, el del proveedor. Vacío, 0 o fuera de 0–100 no cuenta. */
export function elegirPorcConversion(porcCliente: unknown, porcProveedor: unknown): number {
  const cliente = porcConversionEnRango(porcCliente);
  if (cliente > 0) return cliente;
  return porcConversionEnRango(porcProveedor);
}

function porcConversionEnRango(value: unknown): number {
  const n = typeof value === 'string' ? Number(value.replace(',', '.')) : Number(value);
  if (!Number.isFinite(n) || n <= 0 || n > 100) return 0;
  return n;
}

type DetalleParaCombinar = {
  nroItem?: number;
  codigoArticulo?: string | null;
  cantidad?: number | null;
  cantidadB?: number | null;
  porcCantB?: number | null;
  porcSubtotalCantB?: number | null;
};

/**
 * La pantalla de edición es la lista de artículos a convertir.
 * Los renglones nuevos (todavía no guardados) entran igual.
 * En los que ya existen se conserva Cant. conv. y % Conv. del servidor
 * si la pantalla no los trae.
 */
export function combinarDetallesPantalla<T extends DetalleParaCombinar>(
  api: T[],
  pantalla: ReadonlyArray<DetalleParaCombinar> | undefined | null,
): T[] {
  if (!pantalla || pantalla.length === 0) return [...api];
  const usados = new Set<number>();
  return pantalla.map((p) => {
    const codigo = String(p.codigoArticulo ?? '').trim();
    const nro = Number(p.nroItem ?? 0);
    const mismo = (a: T, i: number, exigirItem: boolean) => {
      if (usados.has(i)) return false;
      if (String(a.codigoArticulo ?? '').trim() !== codigo) return false;
      if (!exigirItem) return true;
      return nro > 0 && Number(a.nroItem ?? 0) === nro;
    };
    let idx = api.findIndex((a, i) => mismo(a, i, true));
    if (idx < 0) {
      const porCodigo = api
        .map((a, i) => (mismo(a, i, false) ? i : -1))
        .filter((i) => i >= 0);
      if (porCodigo.length === 1) idx = porCodigo[0];
    }
    if (idx < 0) return p as T;
    usados.add(idx);
    const guardado = api[idx];
    return {
      ...guardado,
      ...p,
      cantidad: p.cantidad ?? guardado.cantidad,
      cantidadB: p.cantidadB != null ? p.cantidadB : guardado.cantidadB,
      porcCantB: p.porcCantB != null ? p.porcCantB : guardado.porcCantB,
      porcSubtotalCantB:
        p.porcSubtotalCantB != null ? p.porcSubtotalCantB : guardado.porcSubtotalCantB,
    } as T;
  });
}
